import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bell, CheckCheck, Clock3, MessageCircle, Phone, Plus, RefreshCw, Search, Send } from 'lucide-react';
import { Card, Status } from './UI';
import { api, dateTime } from '../lib/api';
import WhatsAppComposerModal from './WhatsAppComposerModal';

const post=body=>({method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify(body)});
const title=value=>String(value??'').replaceAll('_',' ').replace(/\b\w/g,char=>char.toUpperCase());
const threadLabel=thread=>thread?.contact_name||thread?.customer_id||thread?.phone_e164||'Unknown contact';

export default function WhatsAppInbox({provider,initialPhone='',initialCustomerId=''}){
  const[snapshot,setSnapshot]=useState({threads:[],notifications:[],calls:[],counts:{},calling:{}});
  const[selectedPhone,setSelectedPhone]=useState(initialPhone||'');
  const[history,setHistory]=useState([]);
  const[tab,setTab]=useState('chats');
  const[filter,setFilter]=useState('all');
  const[query,setQuery]=useState('');
  const[reply,setReply]=useState('');
  const[busy,setBusy]=useState(false);
  const[loadingHistory,setLoadingHistory]=useState(false);
  const[notice,setNotice]=useState('');
  const[assignedTo,setAssignedTo]=useState('');
  const[composeOpen,setComposeOpen]=useState(false);
  const initialised=useRef(false);
  const notifiedIds=useRef(new Set());

  const loadInbox=useCallback(async({quiet=false}={})=>{
    try{
      const body=await api('/api/whatsapp/inbox');
      setSnapshot(body);
      if(!selectedPhone&&body.threads?.length){
        const preferred=(initialPhone&&body.threads.find(row=>row.phone_e164===initialPhone))||
          (initialCustomerId&&body.threads.find(row=>String(row.customer_id||'')===String(initialCustomerId)))||
          body.threads[0];
        setSelectedPhone(preferred?.phone_e164||'');
      }
      const unread=(body.notifications||[]).filter(item=>item.status==='unread');
      if(!initialised.current){
        unread.forEach(item=>notifiedIds.current.add(item.id));
        initialised.current=true;
      }else if(typeof Notification!=='undefined'&&Notification.permission==='granted'){
        for(const item of unread){
          if(notifiedIds.current.has(item.id))continue;
          notifiedIds.current.add(item.id);
          try{
            new Notification(item.title||'New WhatsApp message',{body:item.body||'New message received',tag:item.id});
          }catch{}
        }
      }
      return body;
    }catch(error){
      if(!quiet)setNotice('Error: '+(error.message||'WhatsApp inbox could not load.'));
      return null;
    }
  },[selectedPhone,initialPhone,initialCustomerId]);

  const loadHistory=useCallback(async phone=>{
    if(!phone){setHistory([]);return;}
    setLoadingHistory(true);
    try{
      const body=await api('/api/whatsapp/conversations/history?phone='+encodeURIComponent(phone)+'&limit=200');
      setHistory(body.messages||[]);
    }catch(error){setNotice('Error: '+(error.message||'Conversation history could not load.'));}
    finally{setLoadingHistory(false);}
  },[]);

  useEffect(()=>{
    loadInbox();
    const timer=setInterval(()=>loadInbox({quiet:true}),8000);
    return()=>clearInterval(timer);
  },[loadInbox]);

  useEffect(()=>{
    if(!selectedPhone)return;
    loadHistory(selectedPhone);
    const timer=setInterval(()=>loadHistory(selectedPhone),8000);
    return()=>clearInterval(timer);
  },[selectedPhone,loadHistory]);

  const threads=snapshot.threads||[],notifications=snapshot.notifications||[],calls=snapshot.calls||[],counts=snapshot.counts||{},calling=snapshot.calling||{};
  const selected=threads.find(item=>item.phone_e164===selectedPhone)||null;
  const serviceWindowOpen=Boolean(selected?.last_inbound_at&&Date.now()-Date.parse(selected.last_inbound_at)<24*60*60*1000);

  useEffect(()=>{setAssignedTo(selected?.assigned_to||'');},[selected?.thread_key,selected?.assigned_to]);

  useEffect(()=>{
    if(!selected||Number(selected.unread_count||0)<1)return;
    let active=true;
    api('/api/whatsapp/conversations/read',post({phone:selected.phone_e164}))
      .then(()=>{if(active)loadInbox({quiet:true});})
      .catch(()=>undefined);
    return()=>{active=false;};
  },[selected?.thread_key,selected?.unread_count,selected?.phone_e164,loadInbox]);

  const filteredThreads=useMemo(()=>{
    const term=query.trim().toLowerCase();
    return threads.filter(thread=>{
      if(filter==='unread'&&Number(thread.unread_count||0)<1)return false;
      if(filter==='open'&&thread.status!=='open')return false;
      if(filter==='pending'&&thread.status!=='pending')return false;
      if(filter==='closed'&&thread.status!=='closed')return false;
      if(!term)return true;
      return [thread.contact_name,thread.customer_id,thread.phone_e164,thread.last_message_preview,thread.assigned_to].some(value=>String(value||'').toLowerCase().includes(term));
    });
  },[threads,filter,query]);

  async function sendReply(){
    if(!selected||!reply.trim())return;
    setBusy(true);setNotice('');
    try{
      await api('/api/whatsapp/messages',post({customerId:selected.customer_id||'',phone:selected.phone_e164,type:'text',text:reply.trim(),purpose:'support',source:'crm_whatsapp_inbox'}));
      setReply('');setNotice('Reply sent. Delivery and read status will update automatically.');
      await Promise.all([loadHistory(selected.phone_e164),loadInbox({quiet:true})]);
    }catch(error){setNotice('Error: '+(error.message||'Reply could not be sent.'));}
    finally{setBusy(false);}
  }

  async function updateThread(changes){
    if(!selected)return;
    setBusy(true);setNotice('');
    try{
      await api('/api/whatsapp/conversations/state',post({phone:selected.phone_e164,...changes}));
      await loadInbox({quiet:true});
      setNotice('Conversation updated.');
    }catch(error){setNotice('Error: '+(error.message||'Conversation could not be updated.'));}
    finally{setBusy(false);}
  }

  async function enableDesktopAlerts(){
    if(typeof Notification==='undefined'){setNotice('Desktop notifications are not supported by this browser.');return;}
    const result=await Notification.requestPermission();
    setNotice(result==='granted'?'Desktop WhatsApp notifications enabled.':'Desktop notifications were not enabled.');
  }

  function openNotification(item){
    setSelectedPhone(item.phone_e164);
    setTab('chats');
  }

  return <Card title="WhatsApp inbox">
    <div className="wai-shell">
      <div className="wai-topbar">
        <div className="wai-tabs">
          <button type="button" className={tab==='chats'?'active':''} onClick={()=>setTab('chats')}><MessageCircle size={16}/>Chats{Number(counts.unreadMessages||0)>0&&<span>{counts.unreadMessages}</span>}</button>
          <button type="button" className={tab==='notifications'?'active':''} onClick={()=>setTab('notifications')}><Bell size={16}/>Notifications{Number(counts.unreadNotifications||0)>0&&<span>{counts.unreadNotifications}</span>}</button>
          <button type="button" className={tab==='calls'?'active':''} onClick={()=>setTab('calls')}><Phone size={16}/>Calls</button>
        </div>
        <div className="wai-actions">
          <button type="button" className="button primary govuk-button" onClick={()=>setComposeOpen(true)} disabled={!provider?.outboundReady}><Plus size={15}/>New message</button>
          {typeof Notification!=='undefined'&&Notification.permission!=='granted'&&<button type="button" className="button secondary govuk-button govuk-button--secondary" onClick={enableDesktopAlerts}><Bell size={15}/>Enable desktop alerts</button>}
          <button type="button" className="button secondary govuk-button govuk-button--secondary" onClick={()=>{loadInbox();selectedPhone&&loadHistory(selectedPhone);}}><RefreshCw size={15}/>Refresh</button>
        </div>
      </div>

      {notice&&<div className={'operation-notice '+(notice.startsWith('Error:')?'error':'')}>{notice}</div>}

      {tab==='chats'&&<div className="wai-inbox">
        <aside className="wai-sidebar">
          <div className="wai-search"><Search size={16}/><input className="govuk-input" value={query} onChange={event=>setQuery(event.target.value)} placeholder="Search chats, customers or numbers"/></div>
          <div className="wai-filters">{['all','unread','open','pending','closed'].map(value=><button type="button" key={value} className={filter===value?'active':''} onClick={()=>setFilter(value)}>{title(value)}</button>)}</div>
          <div className="wai-thread-list">
            {filteredThreads.map(thread=><button type="button" key={thread.thread_key} className={'wai-thread '+(thread.phone_e164===selectedPhone?'active':'')} onClick={()=>{setSelectedPhone(thread.phone_e164);setReply('');}}>
              <div className="wai-avatar">{(thread.contact_name||thread.customer_id||thread.phone_e164||'?').slice(0,1).toUpperCase()}</div>
              <div className="wai-thread-copy">
                <div className="wai-thread-line"><strong>{threadLabel(thread)}</strong><small>{thread.last_message_at?dateTime(thread.last_message_at):''}</small></div>
                <div className="wai-thread-line"><span>{thread.last_message_direction==='outbound'?'You: ':''}{thread.last_message_preview||'WhatsApp conversation'}</span>{Number(thread.unread_count||0)>0&&<b className="wai-unread">{thread.unread_count}</b>}</div>
                <div className="wai-thread-meta"><em className={'wai-priority '+(thread.priority||'normal')}>{title(thread.priority||'normal')}</em><span>{thread.assigned_to||'Unassigned'}</span></div>
              </div>
            </button>)}
            {!filteredThreads.length&&<div className="wai-empty-side">No conversations match this view.</div>}
          </div>
        </aside>

        <section className="wai-conversation">
          {selected?<><header className="wai-conversation-head">
            <div className="wai-contact"><div className="wai-avatar large">{threadLabel(selected).slice(0,1).toUpperCase()}</div><div><strong>{threadLabel(selected)}</strong><span>{selected.phone_e164}{selected.customer_id?' · Customer '+selected.customer_id:' · Unlinked'}</span></div></div>
            <div className="wai-thread-controls">
              <label className="govuk-label">Status<select className="govuk-select" value={selected.status||'open'} onChange={event=>updateThread({status:event.target.value})} disabled={busy}><option value="open">Open</option><option value="pending">Pending</option><option value="closed">Closed</option></select></label>
              <label className="govuk-label">Priority<select className="govuk-select" value={selected.priority||'normal'} onChange={event=>updateThread({priority:event.target.value})} disabled={busy}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select></label>
              <label className="govuk-label">Assigned to<div className="wai-assign"><input className="govuk-input" value={assignedTo} onChange={event=>setAssignedTo(event.target.value)} placeholder="Staff email / ID"/><button type="button" className="button secondary govuk-button govuk-button--secondary" onClick={()=>updateThread({assignedTo})} disabled={busy}>Save</button></div></label>
            </div>
          </header>

          <div className="wai-window-strip"><Clock3 size={14}/><span>{serviceWindowOpen?'24-hour customer-service window is open — free-form replies are available.':'24-hour customer-service window is closed — use an approved template to restart the conversation.'}</span></div>

          <div className="wai-messages">
            {loadingHistory&&!history.length?<div className="wai-loading">Loading conversation history…</div>:history.map(message=><article key={message.id||message.whatsapp_message_id} className={'wai-message '+(message.direction==='outbound'?'outbound':'inbound')}>
              <div>{message.body_preview||title(message.message_type)}</div>
              <footer><span>{dateTime(message.received_at||message.sent_at||message.created_at)}</span>{message.direction==='outbound'&&<span className="wai-status"><CheckCheck size={13}/>{title(message.status||'sent')}</span>}</footer>
            </article>)}
            {!loadingHistory&&!history.length&&<div className="wai-loading">No message history for this conversation yet.</div>}
          </div>

          <div className="wai-composer">
            <textarea className="govuk-textarea" rows="3" value={reply} onChange={event=>setReply(event.target.value)} placeholder={serviceWindowOpen?'Write a WhatsApp reply…':'Free-form replies are unavailable outside the 24-hour window.'} maxLength={4096} disabled={!serviceWindowOpen}/>
            <div><small>{provider?.outboundReady?'Live through Meta Cloud API':'Outbound Cloud API messaging is not ready'}</small><button type="button" className="button primary govuk-button" onClick={sendReply} disabled={busy||!provider?.outboundReady||!serviceWindowOpen||!reply.trim()}><Send size={15}/>{busy?'Sending…':'Send reply'}</button></div>
          </div>
          </>:<div className="wai-empty-main"><MessageCircle size={34}/><strong>Select a WhatsApp conversation</strong><span>Conversation history, delivery status and replies will appear here.</span></div>}
        </section>
      </div>}

      {tab==='notifications'&&<div className="wai-notification-panel">
        <header><div><strong>WhatsApp notifications</strong><span>New inbound messages create an unread CRM notification and increment the chat badge.</span></div><Bell size={20}/></header>
        <div className="wai-notification-list">
          {notifications.map(item=><button type="button" key={item.id} className={'wai-notification '+(item.status==='unread'?'unread':'')} onClick={()=>openNotification(item)}>
            <div className="wai-notification-icon"><MessageCircle size={16}/></div>
            <div><strong>{item.title||item.phone_e164}</strong><span>{item.body||'New WhatsApp message'}</span><small>{dateTime(item.created_at)} · {item.status==='unread'?'Unread':'Read'}</small></div>
          </button>)}
          {!notifications.length&&<div className="wai-empty-main"><Bell size={32}/><strong>No WhatsApp notifications yet</strong><span>New inbound messages will appear here automatically.</span></div>}
        </div>
      </div>}

      {tab==='calls'&&<div className="wai-call-panel">
        <div className={'wai-call-capability '+(calling.coexistence?'blocked':'ready')}>
          <Phone size={24}/><div><strong>{calling.coexistence?'WhatsApp Calling API is not available on this coexistence number':'Calling integration can be prepared for an eligible dedicated Cloud API number'}</strong><p className="govuk-body">{calling.reason}</p></div>
        </div>
        {calling.coexistence&&<div className="wai-call-note"><strong>What this means for +44 7886 158834</strong><p className="govuk-body">Calls can continue normally in the WhatsApp Business phone app, but Meta does not expose those app calls to Customer Operations. We therefore cannot automatically answer them in the CRM or import those phone-app call logs while this number stays in coexistence mode.</p><p className="govuk-body">The CRM call-log database and webhook handling are now prepared for a future eligible dedicated Cloud API number. Full browser calling would then add Meta's <b>calls</b> webhook plus WebRTC or SIP media handling.</p></div>}
        <div className="wai-call-list">
          <div className="wai-call-list-head"><strong>WhatsApp API call logs</strong><span>{calls.length} recorded</span></div>
          {calls.map(call=><div className="wai-call-row" key={call.call_id}>
            <div className="wai-call-direction"><Phone size={16}/></div>
            <div><strong>{call.phone_e164||'WhatsApp user'}</strong><span>{title(call.direction)} · {title(call.status||call.event||'Call')}</span></div>
            <div><span>{dateTime(call.ended_at||call.answered_at||call.started_at||call.updated_at)}</span><small>{call.duration_seconds!=null?call.duration_seconds+' sec':''}</small></div>
          </div>)}
          {!calls.length&&<div className="wai-empty-main"><Phone size={32}/><strong>No API call logs</strong><span>{calling.coexistence?'Expected while this number remains in coexistence mode.':'Call events will be stored here once Calling API is enabled and the calls webhook is subscribed.'}</span></div>}
        </div>
      </div>}
      {composeOpen&&<WhatsAppComposerModal
        customerId={initialCustomerId}
        initialPhone={initialPhone}
        close={()=>setComposeOpen(false)}
        source="whatsapp_inbox_new_message"
        onSent={async(_result,sentPhone)=>{
          setComposeOpen(false);
          if(sentPhone)setSelectedPhone(sentPhone);
          setNotice('WhatsApp message sent.');
          await loadInbox({quiet:true});
          if(sentPhone)await loadHistory(sentPhone);
        }}
      />}
    </div>
  </Card>;
}
